/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosRichTextProps } from "../rich-text";
import { CosmosTextProps, getPlainText } from "./CosmosText.atom";
import { CosmosTextAs, CosmosTextVariants, VariantType, CosmosVariableRichText } from "./text.types";

/**
 * Checks if a CosmosVariableRichText has actual content (not just empty HTML tags or whitespace).
 * This utility helps prevent rendering empty elements when content is set to values like `<p></p>`.
 *
 * @param content - The CosmosVariableRichText object to check, or a plain string (for backward compatibility)
 * @returns `true` if the content has meaningful text, `false` otherwise
 *
 * @example
 * ```ts
 * hasContent({ text: "Hello" }) // true
 * hasContent({ html: "<p></p>" }) // false
 * hasContent({ html: "<p>Hello</p>" }) // true
 * hasContent(null) // false
 * hasContent("<p></p>") // false (for string compatibility)
 * ```
 */
export const hasContent = (content: CosmosVariableRichText | string | null | undefined): boolean => {
	if (!content) return false;

	// Handle string input (for backward compatibility with eyebrow field)
	if (typeof content === "string") {
		return getPlainText(content).length > 0;
	}

	// Handle CosmosVariableRichText object
	const hasTextContent = content.text ? content.text.trim().length > 0 : false;
	const hasHtmlContent = content.html ? getPlainText(content.html).length > 0 : false;

	return hasTextContent || hasHtmlContent;
};

export const getTextAs = (props: CosmosTextProps | CosmosRichTextProps): CosmosTextAs | null | undefined => {
	let as = props.as;
	if (!props.as && props.children) {
		if (props.variant?.startsWith("paragraph")) {
			as = "div";
		}
		if (props.variant === "heading2XL") {
			as = "h1";
		}
		if (props.variant === "headingXL") {
			as = "h2";
		}
		if (props.variant === "headingLG") {
			as = "h3";
		}
		if (props.variant === "headingMD") {
			as = "h4";
		}
		if (props.variant === "headingSM") {
			as = "h5";
		}
		if (props.variant === "eyebrowLG") {
			as = "h6";
		}
	}

	return as;
};

export const getTextVariantType = (variant?: CosmosTextVariants | null): VariantType => {
	let variantType: VariantType = "heading";

	if (variant?.includes("paragraph")) {
		variantType = "paragraph";
	} else if (variant?.includes("ui")) {
		variantType = "ui";
	} else if (variant?.includes("eyebrow")) {
		variantType = "eyebrow";
	}

	return variantType;
};
